import { useMemo } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { WidgetViewMode } from 'utilitys/reporting/widgetViewModeUtils';

import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { DEFAULT_QUALITY_CATEGORY } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable';
import { UseLogicQualityCategoriesTableProvider } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable.provider';
import { categoryColumns } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable.utils';

interface Props {
    ui: UseLogicQualityCategoriesTableProvider['ui'];
    api: UseLogicQualityCategoriesTableProvider['api'];
    setIsPayloadReady?: (newValue: boolean) => void;
    widgetViewMode?: WidgetViewMode;
}
export const useLogicQualityCategoriesTableLogic = ({ ui, api, setIsPayloadReady, widgetViewMode }: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.logicCheckerCategoryResults.payload;
        if (!currentPayload) return;
        if (currentPayload.fileMetaDataId === undefined) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    const {
        resolvedProjectId,
        resolvedContractId,
        resolvedCategory,
        selectedVersionId,
        versionName,
        versionDate,
        latestVersion,
    } = ui.vars;
    const { isActive, isDcma, isFailedChecksOnly } = ui.state;
    const { data: summaryData } = api.logicCheckerSummary;
    const { data: logicCheckerData, isLoading, isFetching } = api.logicCheckerCategoryResults.instance;
    const isLoadingTableData = isLoading || isFetching;

    const formattedVersionName = `${versionName} (${moment(versionDate).format(constants.formats.date.default)})`;

    const generatedCols = useMemo(() => {
        return categoryColumns(
            resolvedCategory,
            summaryData?.inScoreNumber,
            resolvedProjectId,
            resolvedContractId,
            formattedVersionName,
            selectedVersionId,
            latestVersion?.versionHashCode,
        );
    }, [resolvedCategory, summaryData]);

    const generatedData = useMemo(() => {
        if (logicCheckerData) {
            const categoryData = logicCheckerData.categoriesSummaryRows.find(
                (item) => item.category === resolvedCategory,
            );

            if (categoryData?.validationRows) {
                return categoryData?.validationRows.filter((item) => {
                    if (isDcma && !item.dcma) return false;
                    if (isFailedChecksOnly && item.status !== 'FAIL') return false;
                    if (isActive && !item.mandatory) return false;
                    return true;
                });
            }
            return [];
        } else {
            return [];
        }
    }, [logicCheckerData, isDcma, isActive, isFailedChecksOnly]);

    const shouldRenderAddToReportButton = resolvedCategory === DEFAULT_QUALITY_CATEGORY;

    return {
        logic: {
            generatedCols,
            generatedData,
            isLoadingTableData,
            shouldRenderAddToReportButton,
        },
    };
};
